import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} ProductPaymentInfoRefs
 * @property {HTMLElement | undefined} installmentsSummary
 * @property {HTMLTableSectionElement | undefined} installmentsTableBody
 * @property {HTMLElement | undefined} dialogPixAmount
 * @property {HTMLElement | undefined} dialogPixCaption
 * @property {HTMLElement | undefined} dialogBoletoAmount
 * @property {HTMLElement | undefined} dialogBoletoCaption
 */

/**
 * A product payment information component with Shopify and manual modes.
 *
 * @extends Component<ProductPaymentInfoRefs>
 */
class ProductPaymentInfo extends Component {
  #abortController = new AbortController();

  /** @type {Record<string, number>} */
  #priceMap = {};

  /** @type {Array<{count: number, interestRate: number}>} */
  #installmentRules = [];

  connectedCallback() {
    super.connectedCallback();

    const parsedPriceMap = this.#parseJsonScript('price-map');
    const parsedInstallmentRules = this.#parseJsonScript('installment-rules');

    this.#priceMap = parsedPriceMap && typeof parsedPriceMap === 'object' && !Array.isArray(parsedPriceMap)
      ? parsedPriceMap
      : {};

    this.#installmentRules = (Array.isArray(parsedInstallmentRules) ? parsedInstallmentRules : [])
      .map((rule) => ({
        count: Number(rule.count) || 0,
        interestRate: Number(rule.interestRate) || 0,
      }))
      .filter((rule) => rule.count > 0)
      .sort((a, b) => a.count - b.count);

    if (this.dataset.mode === 'manual') {
      const { signal } = this.#abortController;
      const target = this.closest('.shopify-section, dialog, product-card');
      target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
      target?.addEventListener('change', this.#onVariantInputChange, { signal });

      this.#render(this.dataset.initialVariantId || this.#findCurrentVariantId());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {string} key
   * @returns {any}
   */
  #parseJsonScript(key) {
    const script = this.querySelector(`[data-${key}]`);
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @returns {string}
   */
  #findCurrentVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.closest('.shopify-section, dialog, product-card')?.querySelector('input[name="id"]'));
    return input?.value || '';
  }

  /**
   * @param {Event} event
   */
  #onVariantInputChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
    this.#render(input.value);
  };

  /**
   * @param {Event & {detail?: {resource?: {id?: string | number}}}} event
   */
  #onVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    this.#render(String(variantId));
  };

  /**
   * @param {string} variantId
   */
  #render(variantId) {
    const price = this.#priceMap[variantId];
    if (!price) return;

    const plans = this.#buildInstallmentPlans(price);
    const highlightPlan = plans.at(-1);

    if (this.refs.installmentsSummary) {
      this.refs.installmentsSummary.textContent = highlightPlan
        ? `${this.#interpolate(
          highlightPlan.interestRate > 0
            ? this.dataset.summaryTemplateInterest
            : this.dataset.summaryTemplateInterestFree,
          {
            count: String(highlightPlan.count),
            amount: this.#formatMoney(highlightPlan.installmentAmount),
            total: this.#formatMoney(highlightPlan.total),
          }
        )}`
        : '';
    }

    const pixDiscount = Number(this.dataset.pixDiscountPercent) || 0;
    if (this.refs.dialogPixAmount || this.refs.dialogPixCaption) {
      const pixDiscountAmount = Math.round((price * pixDiscount) / 100);
      const pixAmount = price - pixDiscountAmount;

      if (this.refs.dialogPixAmount) {
        this.refs.dialogPixAmount.textContent = this.#formatMoney(pixAmount);
      }

      if (this.refs.dialogPixCaption) {
        this.refs.dialogPixCaption.textContent = this.#interpolate(this.dataset.pixCaptionTemplate, {
          amount: this.#formatMoney(pixAmount),
          discount_amount: this.#formatMoney(pixDiscountAmount),
          discount_percent: String(pixDiscount),
        });
      }
    }

    const boletoDiscount = Number(this.dataset.boletoDiscountPercent) || 0;
    if (this.refs.boletoSummary || this.refs.boletoCaption || this.refs.dialogBoletoAmount || this.refs.dialogBoletoCaption) {
      const boletoDiscountAmount = Math.round((price * boletoDiscount) / 100);
      const boletoAmount = price - boletoDiscountAmount;

      if (this.refs.boletoSummary) {
        this.refs.boletoSummary.textContent = this.#interpolate(this.dataset.boletoSummaryTemplate, {
          amount: this.#formatMoney(boletoAmount),
          discount_amount: this.#formatMoney(boletoDiscountAmount),
          discount_percent: String(boletoDiscount),
        });
      }

      if (this.refs.boletoCaption) {
        this.refs.boletoCaption.textContent = this.#interpolate(this.dataset.boletoCaptionTemplate, {
          amount: this.#formatMoney(boletoAmount),
          discount_amount: this.#formatMoney(boletoDiscountAmount),
          discount_percent: String(boletoDiscount),
        });
      }

      if (this.refs.dialogBoletoAmount) {
        this.refs.dialogBoletoAmount.textContent = this.#formatMoney(boletoAmount);
      }

      if (this.refs.dialogBoletoCaption) {
        this.refs.dialogBoletoCaption.textContent = this.#interpolate(this.dataset.boletoCaptionTemplate, {
          amount: this.#formatMoney(boletoAmount),
          discount_amount: this.#formatMoney(boletoDiscountAmount),
          discount_percent: String(boletoDiscount),
        });
      }
    }

    this.#renderInstallmentsTable(plans);
  }

  /**
   * @param {number} basePrice
   * @returns {Array<{count: number, interestRate: number, total: number, installmentAmount: number}>}
   */
  #buildInstallmentPlans(basePrice) {
    return this.#installmentRules.map((rule) => {
      const total = Math.round(basePrice * (1 + rule.interestRate / 100));
      return {
        count: rule.count,
        interestRate: rule.interestRate,
        total,
        installmentAmount: Math.round(total / rule.count),
      };
    });
  }

  /**
   * @param {Array<{count: number, interestRate: number, total: number, installmentAmount: number}>} plans
   */
  #renderInstallmentsTable(plans) {
    const tbody = this.refs.installmentsTableBody;
    if (!tbody) return;

    tbody.replaceChildren(
      ...plans.map((plan) => {
        const row = document.createElement('tr');

        const installmentCell = document.createElement('td');
        installmentCell.className = 'product-payment-info__table-installment';
        installmentCell.textContent = this.#interpolate(
          plan.interestRate > 0 ? this.dataset.installmentTemplateInterest : this.dataset.installmentTemplateInterestFree,
          {
            count: String(plan.count),
            amount: this.#formatMoney(plan.installmentAmount),
            total: this.#formatMoney(plan.total),
          }
        );

        const totalCell = document.createElement('td');
        totalCell.className = 'product-payment-info__table-total';
        totalCell.textContent = this.#formatMoney(plan.total);

        row.append(installmentCell, totalCell);
        return row;
      })
    );
  }

  /**
   * @param {string | undefined} template
   * @param {Record<string, string>} values
   * @returns {string}
   */
  #interpolate(template, values) {
    const safeTemplate = template || '';
    return safeTemplate.replace(/\[([a-z_]+)\]/gi, (_, key) => values[key] ?? '');
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  #formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }
}

if (!customElements.get('product-payment-info')) {
  customElements.define('product-payment-info', ProductPaymentInfo);
}
